import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Country } from '../../countriesConfig';
import {
  ContactPhoneCaptureService,
  ContactPhoneSource,
} from '../contact-phone-capture.service';

export type JourneyContactPhoneVariant = 'ready' | 'proof' | 'fair';

@Component({
  selector: 'app-journey-contact-phone-strip',
  templateUrl: './journey-contact-phone-strip.component.html',
  styleUrls: ['./journey-contact-phone-strip.component.css'],
  imports: [CommonModule, FormsModule, MatSelectModule, MatSnackBarModule],
})
export class JourneyContactPhoneStripComponent implements OnChanges {
  @Input() title = '';
  @Input() label = 'Mobile number';
  @Input() hint = '';
  @Input() skipLabel = 'Not now';
  @Input() showSkip = false;
  /** When false, hides the Recommended / Saved pills beside the label. */
  @Input() showStatusPills = true;
  @Input() variant: JourneyContactPhoneVariant = 'proof';
  @Input() nudgeVisible = false;
  @Input() nudgeCopy = 'Add your mobile, then continue.';
  @Input() source: ContactPhoneSource = 'ready';
  /** Prefill from parent (saved E.164 value). */
  @Input() phoneValue = '';

  @Output() saved = new EventEmitter<string>();
  @Output() skipped = new EventEmitter<void>();
  @Output() phoneChange = new EventEmitter<string>();

  @ViewChild('phoneInput') phoneInput?: ElementRef<HTMLInputElement>;

  countries: Country[] = [];
  dialCode = '+91';
  national = '';
  phoneSaved = '';
  status: 'idle' | 'saving' | 'saved' | 'error' | 'invalid' = 'idle';
  phoneRowFocused = false;
  private savedStatusTimer: ReturnType<typeof setTimeout> | null = null;
  readonly inputId = `journey-contact-phone-${Math.random().toString(36).slice(2, 9)}`;

  constructor(
    private contactPhoneCapture: ContactPhoneCaptureService,
    private snackBar: MatSnackBar
  ) {
    this.countries = this.contactPhoneCapture.getCountries();
    this.dialCode = this.contactPhoneCapture.defaultDialCode;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['phoneValue']) {
      const next = this.contactPhoneCapture.normalizePhone(this.phoneValue);
      this.phoneSaved = next;
      if (this.status !== 'saving') {
        const parts = this.contactPhoneCapture.parsePhoneParts(next);
        this.dialCode = parts.dialCode || this.contactPhoneCapture.defaultDialCode;
        this.national = parts.national;
      }
    }
  }

  get displayPhone(): string | null {
    const phone = String(this.phoneSaved || '').trim();
    return phone || null;
  }

  get dialDisplay(): string {
    const match = this.countries.find((c) => c.dialCode === this.dialCode);
    if (match) {
      return `${match.code} ${match.dialCode}`;
    }
    return this.dialCode || '+91';
  }

  get composedDraft(): string {
    return this.contactPhoneCapture.composeE164(this.dialCode, this.national);
  }

  focusInput(): void {
    setTimeout(() => this.phoneInput?.nativeElement?.focus(), 0);
  }

  flush(): void {
    this.commitFromEdit();
  }

  onFocus(): void {
    // keep Ready soft-nudge editing open
  }

  onDialChange(dial: string): void {
    this.dialCode = this.contactPhoneCapture.normalizeDialCode(dial);
    this.emitDraft();
    if (this.status === 'invalid') {
      this.status = 'idle';
    }
  }

  onNationalChange(value: string): void {
    this.national = String(value || '').replace(/[^\d\s-]/g, '');
    this.emitDraft();
    if (this.status === 'invalid') {
      this.status = 'idle';
    }
  }

  private emitDraft(): void {
    this.phoneChange.emit(this.composedDraft);
  }

  commitFromEdit(): void {
    const composed = this.composedDraft;
    const previous = String(this.phoneSaved || '').trim();
    if (composed === previous) {
      this.status = 'idle';
      return;
    }
    this.commit();
  }

  cancelEdit(): void {
    const parts = this.contactPhoneCapture.parsePhoneParts(this.phoneSaved);
    this.dialCode = parts.dialCode || this.contactPhoneCapture.defaultDialCode;
    this.national = parts.national;
    this.status = 'idle';
    this.emitDraft();
  }

  skip(): void {
    this.contactPhoneCapture.trackSkipped(this.source);
    this.skipped.emit();
  }

  commit(): void {
    if (this.status === 'saving') {
      return;
    }
    const composed = this.composedDraft;
    const previous = String(this.phoneSaved || '').trim();
    if (composed === previous) {
      this.status = 'idle';
      return;
    }
    if (composed && !this.contactPhoneCapture.isValidPhone(composed)) {
      this.status = 'invalid';
      return;
    }

    this.status = 'saving';
    this.contactPhoneCapture.savePhone(composed, this.source).subscribe({
      next: () => {
        this.phoneSaved = composed;
        const parts = this.contactPhoneCapture.parsePhoneParts(composed);
        this.dialCode = parts.dialCode || this.contactPhoneCapture.defaultDialCode;
        this.national = parts.national;
        this.status = 'saved';
        this.saved.emit(composed);
        if (this.savedStatusTimer) {
          clearTimeout(this.savedStatusTimer);
        }
        this.savedStatusTimer = setTimeout(() => {
          if (this.status === 'saved') {
            this.status = 'idle';
          }
          this.savedStatusTimer = null;
        }, 1200);
      },
      error: (err) => {
        if (err?.message === 'invalid_phone') {
          this.status = 'invalid';
          return;
        }
        this.status = 'error';
        this.snackBar.open('Could not save mobile number. Try again.', 'OK', {
          duration: 4000,
        });
      },
    });
  }
}
